import { pipelineCapacityDisplay } from '../lib/pipeline-capacity';
import React, { useState, useEffect, useMemo } from 'react';
import { REPORT, SectionTitle, InfoRow, SourceFootnote } from './print/PrintAtoms';
import { getDCScoreColor, getDCScoreTier } from '../styles/design-tokens';
import { fetchAssemblyDetail, type AssemblyDetailResponse } from '../lib/api';

const fmtNum = (n: number | null | undefined) =>
  n != null ? Math.round(Number(n)).toLocaleString() : '\u2014';

const fmtMiles = (n: number | null | undefined) =>
  n != null ? `${Number(n).toFixed(1)} mi` : '\u2014';

// Acreage tier — same as AssembliesReport
function getAcreageTier(acres: number): { label: string; color: string } {
  if (acres >= 10000) return { label: 'MEGA', color: '#16a34a' };
  if (acres >= 2500)  return { label: 'BTM SCALE', color: '#2563eb' };
  if (acres >= 1000)  return { label: 'MODERATE', color: '#ca8a04' };
  return { label: 'SMALL', color: '#6A7E8E' };
}

const printStyles = `
@media print {
  .assembly-detail-toolbar { display: none !important; }
  @page { margin: 0.4in; }
  .assembly-detail-section { page-break-inside: avoid; }
}
.parcel-row-clickable:hover { background: rgba(106,126,142,0.10) !important; }
`;

const TH: React.CSSProperties = {
  padding: '4px 8px', fontWeight: 600, fontSize: '8px',
  textTransform: 'uppercase', letterSpacing: '0.06em',
  color: REPORT.secondary, background: REPORT.surface,
  fontFamily: 'monospace',
};

interface AssemblyDetailProps {
  assemblyId: string;
  onClose: () => void;
  onViewOnMap?: (lat: number, lng: number, tight?: boolean) => void;
}

export const AssemblyDetail: React.FC<AssemblyDetailProps> = ({
  assemblyId, onClose, onViewOnMap,
}) => {
  const [data, setData] = useState<AssemblyDetailResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    setError(null);
    let cancelled = false;
    fetchAssemblyDetail(assemblyId)
      .then(d => { if (!cancelled) setData(d); })
      .catch(() => { if (!cancelled) setError('Failed to load assembly detail'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [assemblyId]);

  const group = data?.group;
  const parcels = data?.parcels || [];
  const pipelines = data?.pipelines || [];

  const tier = useMemo(() => group ? getAcreageTier(Number(group.total_acreage)) : null, [group]);

  // Parcel aggregates
  const parcelStats = useMemo(() => {
    if (parcels.length === 0) return null;
    const withScore = parcels.filter(p => p.dc_suitability_score != null);
    const avgScore = withScore.length > 0
      ? withScore.reduce((s, p) => s + Number(p.dc_suitability_score), 0) / withScore.length
      : null;
    const withSlope = parcels.filter(p => p.avg_slope_pct != null);
    const avgSlope = withSlope.length > 0
      ? withSlope.reduce((s, p) => s + Number(p.avg_slope_pct), 0) / withSlope.length
      : null;
    const fiberCount = parcels.filter(p => p.fiber_available).length;
    const floodCount = parcels.filter(p => p.flood_zone && /^A|^V/i.test(p.flood_zone)).length;
    return { avgScore, avgSlope, fiberCount, floodCount };
  }, [parcels]);

  // Pipeline summary
  const pipelineSummary = useMemo(() => {
    if (pipelines.length === 0) return null;
    const within5 = pipelines.filter(p => Number(p.distance_miles) <= 5);
    const withCapacity = within5.filter(p => (pipelineCapacityDisplay(p).capacityMmcf ?? 0) > 0);
    const operators = [...new Set(within5.map(p => p.operator).filter(Boolean))];
    return { total: pipelines.length, within5: within5.length, withCapacity: withCapacity.length, operators };
  }, [pipelines]);

  const sectionStyle: React.CSSProperties = { marginBottom: '24px' };

  return (
    <div style={{
      display: 'flex', flexDirection: 'column', height: '100vh', width: '100vw',
      fontFamily: '"DM Sans", -apple-system, sans-serif',
      background: REPORT.white, color: REPORT.heading,
    }}>
      <style>{printStyles}</style>

      {/* Toolbar */}
      <div className="assembly-detail-toolbar" style={{
        height: '40px', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '0 20px', background: REPORT.heading, borderBottom: `1px solid ${REPORT.rule}`, flexShrink: 0,
      }}>
        <button onClick={onClose} style={{
          display: 'flex', alignItems: 'center', gap: '6px',
          background: 'none', border: 'none', color: REPORT.secondary,
          cursor: 'pointer', fontSize: '12px', fontFamily: '"DM Sans", sans-serif',
        }}>
          &larr; Back to Assemblies
        </button>
        <div style={{ display: 'flex', gap: '8px' }}>
          {group && onViewOnMap && (
            <button
              onClick={() => onViewOnMap(Number(group.centroid_lat), Number(group.centroid_lng))}
              style={{
                padding: '4px 14px', background: 'none', color: REPORT.secondary,
                border: `1px solid rgba(80,98,114,0.3)`, borderRadius: '3px', fontSize: '11px',
                fontWeight: 500, cursor: 'pointer', fontFamily: '"DM Sans", sans-serif',
              }}
            >
              View on Map
            </button>
          )}
          <button onClick={() => window.print()} style={{
            padding: '4px 14px', background: REPORT.accent, color: '#E4E6E8',
            border: 'none', borderRadius: '3px', fontSize: '11px', fontWeight: 600,
            cursor: 'pointer', fontFamily: '"DM Sans", sans-serif',
          }}>
            Print
          </button>
        </div>
      </div>

      {/* Scrollable content */}
      <div style={{ flex: 1, overflow: 'auto' }}>
        <div style={{ maxWidth: '1000px', margin: '0 auto', padding: '32px 28px 64px' }}>

          {loading && (
            <div style={{ textAlign: 'center', padding: '80px 0', color: REPORT.secondary }}>
              Loading assembly detail...
            </div>
          )}

          {error && (
            <div style={{ textAlign: 'center', padding: '80px 0', color: REPORT.fail }}>{error}</div>
          )}

          {group && !loading && (
            <>
              {/* Header */}
              <div className="assembly-detail-section" style={{ ...sectionStyle, marginBottom: '28px' }}>
                <div style={{
                  fontSize: '9px', fontWeight: 500, color: REPORT.accent,
                  textTransform: 'uppercase', letterSpacing: '0.12em',
                  fontFamily: 'monospace',
                }}>
                  Assembly Detail
                </div>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: '12px', flexWrap: 'wrap' }}>
                  <div style={{ fontSize: '22px', fontWeight: 400, color: REPORT.heading, fontFamily: 'Georgia, serif', lineHeight: 1.3 }}>
                    {group.owner_name || 'Unknown Owner'}
                  </div>
                  {tier && (
                    <span style={{
                      padding: '3px 10px', borderRadius: '4px', fontSize: '9px', fontWeight: 700,
                      background: tier.color + '18', color: tier.color,
                      fontFamily: 'monospace', letterSpacing: '0.04em',
                    }}>
                      {tier.label} &middot; ~{fmtNum(group.total_acreage)} acres
                    </span>
                  )}
                </div>
                <div style={{ fontSize: '11px', color: REPORT.secondary, marginTop: '4px' }}>
                  {group.counties?.join(', ') || group.county}, {group.state}
                  &nbsp;&middot; {group.parcel_count} parcels
                  &nbsp;&middot; {Number(group.centroid_lat).toFixed(4)}, {Number(group.centroid_lng).toFixed(4)}
                  {group.nearest_pipeline_miles != null && (
                    <> &nbsp;&middot; Nearest pipeline: {Number(group.nearest_pipeline_miles).toFixed(1)} mi</>
                  )}
                </div>
              </div>

              {/* Summary cards — grade-card style */}
              <div className="assembly-detail-section" style={sectionStyle}>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '10px' }}>
                  {/* Acreage card */}
                  {tier && (
                    <SummaryCard color={tier.color} title="Land Assembly">
                      <InfoRow label="Total Acreage" value={`~${fmtNum(group.total_acreage)}`} />
                      <InfoRow label="Parcel Count" value={group.parcel_count} />
                      <InfoRow label="Counties" value={group.counties?.join(', ') || group.county} />
                      {parcelStats?.avgSlope != null && (
                        <InfoRow label="Avg Slope" value={`${parcelStats.avgSlope.toFixed(1)}%`} />
                      )}
                    </SummaryCard>
                  )}

                  {/* Pipeline access card */}
                  <SummaryCard
                    color={pipelineSummary && pipelineSummary.within5 > 0 ? '#16a34a' : '#6A7E8E'}
                    title="Pipeline Access"
                  >
                    <InfoRow label="Within 10 mi" value={`${pipelines.length} pipelines`} />
                    {pipelineSummary && (
                      <>
                        <InfoRow label="Within 5 mi" value={pipelineSummary.within5} />
                        <InfoRow label="With Capacity" value={pipelineSummary.withCapacity} />
                        {pipelineSummary.operators.length > 0 && (
                          <InfoRow label="Operators" value={pipelineSummary.operators.length} />
                        )}
                      </>
                    )}
                  </SummaryCard>

                  {/* Site quality card */}
                  <SummaryCard
                    color={parcelStats?.avgScore != null && parcelStats.avgScore >= 60 ? '#2563eb' : '#6A7E8E'}
                    title="Site Quality"
                  >
                    {parcelStats?.avgScore != null && (
                      <>
                        <InfoRow label="Avg DC Score" value={Math.round(parcelStats.avgScore)} />
                        <p style={{ fontSize: '11px', lineHeight: 1.5, color: REPORT.secondary }}>
                          Gas delivery is unverified. Scores retain the existing screening method while the gas model is recalibrated.
                        </p>
                      </>
                    )}
                    {parcelStats && (
                      <>
                        <InfoRow label="Fiber Coverage" value={`${parcelStats.fiberCount}/${parcels.length}`} />
                        {parcelStats.floodCount > 0 && (
                          <InfoRow label="Flood Risk Parcels" value={parcelStats.floodCount} />
                        )}
                      </>
                    )}
                  </SummaryCard>
                </div>
              </div>

              {/* Pipeline access table */}
              {pipelines.length > 0 && (
                <div className="assembly-detail-section" style={sectionStyle}>
                  <SectionTitle>Pipeline Infrastructure ({pipelines.length})</SectionTitle>
                  <div style={{ background: 'white', borderRadius: '6px', overflow: 'hidden', border: `1px solid ${REPORT.rule}`, marginTop: '8px' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '10.5px', tableLayout: 'fixed' }}>
                      <colgroup>
                        <col style={{ width: '26%' }} />
                        <col style={{ width: '20%' }} />
                        <col style={{ width: '10%' }} />
                        <col style={{ width: '14%' }} />
                        <col style={{ width: '15%' }} />
                      </colgroup>
                      <thead>
                        <tr style={{ borderBottom: `1px solid ${REPORT.rule}` }}>
                          <th style={{ ...TH, textAlign: 'left' }}>Pipeline</th>
                          <th style={{ ...TH, textAlign: 'left' }}>Operator</th>
                          <th style={{ ...TH, textAlign: 'right' }}>Diameter</th>
                          <th style={{ ...TH, textAlign: 'right' }}>Published capacity</th>
                          <th style={{ ...TH, textAlign: 'right' }}>Distance</th>
                        </tr>
                      </thead>
                      <tbody>
                        {pipelines.map((pl, i) => (
                          <tr key={pl.id + '-' + i} title={`${pipelineCapacityDisplay(pl).label}: ${pipelineCapacityDisplay(pl).explanation}`} style={{
                            borderBottom: i < pipelines.length - 1 ? `1px solid ${REPORT.rule}80` : 'none',
                            background: i % 2 === 1 ? `${REPORT.surface}40` : 'transparent',
                          }}>
                            <td style={{ padding: '5px 8px', fontWeight: 500, color: REPORT.heading, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {pl.name || 'Unknown'}
                            </td>
                            <td style={{ padding: '5px 8px', color: REPORT.secondary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {pl.operator || '\u2014'}
                            </td>
                            <td style={{ padding: '5px 8px', textAlign: 'right', fontFamily: '"JetBrains Mono", monospace', color: REPORT.body }}>
                              {pl.diameter_inches ? `${pl.diameter_inches}\u2033` : '\u2014'}
                            </td>
                            <td style={{ padding: '5px 8px', textAlign: 'right', fontFamily: '"JetBrains Mono", monospace', color: REPORT.body }}>
                              {pipelineCapacityDisplay(pl).capacityMmcf != null ? `${fmtNum(pipelineCapacityDisplay(pl).capacityMmcf)} MMcf/d` : '\u2014'}
                            </td>
                            <td style={{ padding: '5px 8px', textAlign: 'right', fontFamily: '"JetBrains Mono", monospace', color: REPORT.body }}>
                              {fmtMiles(pl.distance_miles)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <SourceFootnote>
                    Source: {group.state === 'TX' ? 'TX RRC pipeline permits, ' : ''}EIA natural gas pipelines, PHMSA NPMS. Published capacity values are shown only when explicitly sourced and remain scoped to the named permit or project; diameter is physical context, not a capacity estimate. Distance measured from assembly boundary to nearest pipeline segment.
                  </SourceFootnote>
                </div>
              )}

              {/* Member parcels table */}
              {parcels.length > 0 && (
                <div className="assembly-detail-section" style={sectionStyle}>
                  <SectionTitle>Member Parcels ({parcels.length})</SectionTitle>
                  <div style={{ background: 'white', borderRadius: '6px', overflow: 'hidden', border: `1px solid ${REPORT.rule}`, marginTop: '8px' }}>
                    <div style={{ overflowX: 'auto' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '10.5px', minWidth: '700px' }}>
                        <thead>
                          <tr style={{ borderBottom: `1px solid ${REPORT.rule}` }}>
                            <th style={{ ...TH, textAlign: 'center', width: '30px' }}>#</th>
                            <th style={TH}>Owner</th>
                            <th style={{ ...TH, textAlign: 'right' }}>Acres</th>
                            <th style={{ ...TH, textAlign: 'center' }}>DC Score</th>
                            <th style={{ ...TH, textAlign: 'right' }}>Slope</th>
                            <th style={{ ...TH, textAlign: 'center' }}>Infra</th>
                          </tr>
                        </thead>
                        <tbody>
                          {parcels.map((p, i) => {
                            const dcScore = p.dc_suitability_score != null ? Number(p.dc_suitability_score) : 0;
                            const canNavigate = onViewOnMap && p.lat != null && p.lng != null;
                            return (
                              <tr key={p.id}
                                className={canNavigate ? 'parcel-row-clickable' : undefined}
                                onClick={canNavigate ? () => onViewOnMap(Number(p.lat), Number(p.lng), true) : undefined}
                                title={canNavigate ? 'Click to view on map' : undefined}
                                style={{
                                borderBottom: i < parcels.length - 1 ? `1px solid ${REPORT.rule}80` : 'none',
                                background: i % 2 === 1 ? `${REPORT.surface}40` : 'transparent',
                                cursor: canNavigate ? 'pointer' : 'default',
                              }}>
                                <td style={{ padding: '5px 8px', textAlign: 'center', color: REPORT.tertiary, fontFamily: '"JetBrains Mono", monospace', fontSize: '9px' }}>{i + 1}</td>
                                <td style={{ padding: '5px 8px', fontWeight: 500, color: REPORT.heading, maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                  {p.owner_name}
                                </td>
                                <td style={{ padding: '5px 8px', textAlign: 'right', fontFamily: '"JetBrains Mono", monospace', color: REPORT.body }}>
                                  {p.acreage != null ? Number(p.acreage).toFixed(0) : '\u2014'}
                                </td>
                                <td style={{ padding: '5px 8px', textAlign: 'center' }}>
                                  {p.dc_suitability_score != null ? (
                                    <span style={{
                                      display: 'inline-block', padding: '2px 6px', borderRadius: '3px',
                                      fontSize: '8px', fontWeight: 700, fontFamily: 'monospace',
                                      background: getDCScoreColor(dcScore) + '18', color: getDCScoreColor(dcScore),
                                    }}>
                                      {getDCScoreTier(dcScore)} {dcScore}
                                    </span>
                                  ) : '\u2014'}
                                </td>
                                <td style={{ padding: '5px 8px', textAlign: 'right', fontFamily: '"JetBrains Mono", monospace', color: REPORT.body }}>
                                  {p.avg_slope_pct != null ? `${Number(p.avg_slope_pct).toFixed(1)}%` : '\u2014'}
                                </td>
                                <td style={{ padding: '5px 8px', textAlign: 'center', fontSize: '10px', fontFamily: 'monospace' }}>
                                  <span style={{ color: p.fiber_available ? REPORT.pass : REPORT.tertiary }} title="Fiber">{p.fiber_available ? 'F' : '\u2014'}</span>
                                  {' '}
                                  <span style={{ color: p.flood_zone && /^A|^V/i.test(p.flood_zone) ? REPORT.fail : REPORT.pass }} title="Flood">
                                    {p.flood_zone && /^A|^V/i.test(p.flood_zone) ? '!' : '\u2713'}
                                  </span>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}

              {/* Footer */}
              <div style={{
                textAlign: 'center', color: REPORT.tertiary, fontSize: '9px',
                borderTop: `1px solid ${REPORT.rule}`, paddingTop: '16px',
                fontFamily: 'monospace',
              }}>
                Assembly Detail &middot; {group.owner_name} &middot; {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })} &middot; Confidential
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

/* --- Sub-components --- */

const SummaryCard: React.FC<{
  color: string; title: string; children: React.ReactNode;
}> = ({ color, title, children }) => (
  <div style={{
    background: color + '0a',
    borderLeft: `3px solid ${color}`,
    borderRadius: '6px',
    padding: '12px 14px',
  }}>
    <div style={{
      fontSize: '9px', fontWeight: 600, color,
      textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '8px',
      fontFamily: 'monospace',
    }}>
      {title}
    </div>
    {children}
  </div>
);
